/* eslint-disable complexity */
import styled, { css } from 'styled-components';

import variables from '../../../../styles/variables';
import ButtonLink from '../../ButtonLink/ButtonLink';
import Image from '../../Image/Image';

interface BannerCountProps {
  readonly $bannerCount?: number;
}

interface BannerProps {
  readonly $index?: number;
  readonly $bannerCount?: number;
  readonly $buttonColor?: string;
}

interface ContentWrapperProps {
  readonly $index?: number;
  readonly $bannerCount?: number;
  readonly $bgColor?: string;
  readonly $color?: string;
  readonly $imagePosition?: string;
}

interface ImageWrapperProps {
  readonly $index?: number;
  readonly $bannerCount?: number;
  readonly $bgColor?: string;
  readonly $imagePosition?: string;
}

interface CtaContainerProps {
  readonly $index?: number;
  readonly $bannerCount?: number;
  readonly $imagePosition?: string;
}

export const Wrapper = styled.section`
  display: flex;
  flex-direction: unset;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  width: 100%;
  position: relative;
`;

export const Container = styled.div<BannerCountProps>`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  position: relative;
  display: grid;
  flex-direction: column;
  flex-wrap: wrap;
  max-width: ${variables.defaultWidth}px;
  padding: 0 ${variables.layoutPadding}rem;

  ${({ $bannerCount }) => ($bannerCount === 4
      && css`
        // 4 banners
        gap: 20px;
        grid-template:
          'one two'
          'three four';
        grid-template-columns: 50%;

        @media screen and (max-width: 1023px) {
          grid-template:
          'one'
          'two'
          'three'
          'four';
          grid-template-columns: 100%;
        }
      `)
    || ($bannerCount === 3
      && css`
        // 3 banners
        gap: 20px;
        grid-template:
          'one one'
          'two three';
          grid-template-columns: 50%;

        @media screen and (max-width: 1023px) {
          grid-template:
          'one'
          'two'
          'three';
          grid-template-columns: 100%;
        }
      `)
    || ($bannerCount === 2
      && css`
        // 2 banners
        gap: 20px;
        grid-template:
          'one two';
        grid-template-columns: 50%;

        @media screen and (max-width: 1023px) {
          grid-template:
          'one'
          'two';
          grid-template-columns: 100%;
        }
      `)
    || ($bannerCount === 1
      && css`
        // 1 banner
        grid-template:
          'one';
        grid-template-columns: 100%;
      `)}

  @media screen and (max-width: 1023px) {
    padding: 0 ${variables.mobileLayoutPadding}rem;
    flex-wrap: wrap;
    flex-direction: column;
  }
`;

export const Banner = styled(ButtonLink)<BannerProps>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: stretch;
  position: relative;
  align-items: flex-start;
  position: relative;
  max-width: 100%;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  z-index: 1;
  margin: 0;
  grid-area: one;

  // Button overrides
  box-shadow: 2px 4px 12px 0px rgba(0, 0, 0, 0.08) !important;
  transition: all 0.5s !important;
  height: auto !important;
  background-color: transparent !important;
  background: none !important;
  padding: 0 !important;

  ${({ $index }) => ($index === 3
      && css`
      // 4th banner
        grid-area: four;
      `)
    || ($index === 2
      && css`
        // 3rd banner
        grid-area: three;
      `)
    || ($index === 1
      && css`
        // 2nd banner
        grid-area: two;
      `)
    || ($index === 0
      && css`
        grid-area: one;
      `)
}

  ${({ $index, $bannerCount }) => (($bannerCount === 2 || $bannerCount === 4)
      && css`
        flex-direction: row;

        @media screen and (max-width: 1023px) {
          max-width: 100%;
          justify-content: space-between;
        }

        @media screen and (max-width: 640px) {
          flex-direction: column;
          justify-content: center;
          height: auto;
        }
      `)
    || ($bannerCount === 1
      && css`
        flex-direction: row;
        align-items: stretch;

        @media screen and (max-width: 640px) {
          flex-direction: column;
        }
      `)
    || (($bannerCount === 3 && ($index === 1 || $index === 2))
      && css`
        flex-direction: row;

        @media screen and (max-width: 1023px) {
          max-width: 100%;
          justify-content: space-between;
        }

        @media screen and (max-width: 640px) {
          flex-direction: column;
          justify-content: center;
          height: auto;
        }
      `) || (($bannerCount === 3 && ($index === 0) && css`
          flex-direction: row;
          @media screen and (max-width: 640px) {
            flex-direction: column;
          }
        `))}

  &:hover,
  &:focus {
    z-index: 2;
    box-shadow: 0px 10px 30px 0px rgba(0, 0, 0, 0.15) !important;

    div {
      &.cta-button {
        &.red {
          background-color: ${variables.colorRedHover};
          color: ${variables.colorWhite};
        }
        &.black {
          background-color: ${variables.colorBlackHover};
          color: ${variables.colorWhite};
        }
        &.white {
          background-color: ${variables.colorWhiteHover};
          color: ${variables.colorBlackHover};
        }
        &.outline {
          background-color: ${variables.colorWhiteHover};
          color: ${variables.colorBlackHover};
          border: 1px solid ${variables.colorBlackHover};
        }
      }
    }
  }

  div {
    &.cta-button {
      display: flex;
      justify-content: center;
      align-items: center;
      text-align:center;
      text-decoration: none;
      margin-bottom: 0;
      width: auto;
      padding: 12px 16px;
      border-radius: 20px;
      font-weight: 500;
      font-size: 16px;
      line-height: 16px;
      letter-spacing: 0.1px;
      transition:
        background-color 0.3s,
        color 0.3s;

      &.red {
        background-color: ${variables.colorRed};
        color: ${variables.colorWhite};
      }
      &.black {
        background-color: ${variables.colorBlack};
        color: ${variables.colorWhite};
      }
      &.white {
        background-color: ${variables.colorWhite};
        color: ${variables.colorBlack};
      }
      &.outline {
        background-color: ${variables.colorWhite};
        color: ${variables.colorBlack};
        border: 1px solid ${variables.colorBlack};
      }
    }

    &.cta-text-link {
      font-weight: 600;
      font-size: 16px;
      line-height: 18px;
      text-decoration: underline;
      margin-bottom: 0;
      cursor: pointer;

      &.black {
        color: ${variables.colorBlackAlt};
      }

      &.white {
        color: ${variables.colorWhite};
      }

      &.red {
        color: ${variables.colorRed};
      }
    }
  }
`;

export const BannerUnlinked = styled.div<BannerProps>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  align-items: stretch;
  position: relative;
  max-width: 100%;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  z-index: 1;
  margin: 0;
  box-shadow: 2px 4px 12px 0px rgba(0, 0, 0, 0.08);
  transition: all 0.5s;
  grid-area: one;

  &.hero-carousel {
    ${({ $bannerCount }) => ($bannerCount === 1 && css`
        align-items: normal;
    `)}
  }

  ${({ $index }) => ($index === 3
      && css`
      // 4th banner
        grid-area: four;
      `)
    || ($index === 2
      && css`
        // 3rd banner
        grid-area: three;
      `)
    || ($index === 1
      && css`
        // 2nd banner
        grid-area: two;
      `)
    || ($index === 0
      && css`
        grid-area: one;
      `)
}

  ${({ $index, $bannerCount }) => (($bannerCount === 2 || $bannerCount === 4)
      && css`
        flex-direction: row;

        @media screen and (max-width: 1023px) {
          max-width: 100%;
          justify-content: space-between;
        }

        @media screen and (max-width: 640px) {
          flex-direction: column;
          justify-content: center;
          height: auto;
        }
      `)
    || ($bannerCount === 1
      && css`
        flex-direction: row;

        @media screen and (max-width: 640px) {
          flex-direction: column;
        }
      `)
    || (($bannerCount === 3 && ($index === 1 || $index === 2))
      && css`
        flex-direction: row;

        @media screen and (max-width: 1023px) {
          max-width: 100%;
          justify-content: space-between;
        }

        @media screen and (max-width: 640px) {
          flex-direction: column;
          justify-content: center;
          height: auto;
        }
      `)}
`;

export const ContentWrapper = styled.div<ContentWrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  max-width: 100%;
  height: 100%;
  position: relative;
  background-color: ${(props) => props.$bgColor ? variables[props.$bgColor] : variables.colorGreyLight} !important;
  padding: 1.5rem;
  text-align: left;
  z-index: 1;
  width: 50%;
  order: 1;

  ${({ $index, $bannerCount }) => ($bannerCount === 1
      && css`
        height: auto !important;
        padding: 1.5rem;

        @media screen and (max-width: 640px) {
          padding: 1.5rem;
        }
      `)
    || ($index === 0 && $bannerCount === 3
      && css`
        width: 100%;
        order: 1;

        @media screen and (max-width: 640px) {
          width: 100%;
          order: 2;
        }
      `)}

  @media screen and (max-width: 640px) {
    width: auto;
    padding: 1.5rem;
    order: 2;
  }

  div {
    color: ${(props) => props.$color ? variables[props.$color] : variables.colorWhite};
    margin-bottom: 10px;
    text-transform: none;
    width: 100%;

    &.title-heading {
      width: 100%;
      font-size: 36px;
      line-height: 40px;
      font-weight: 600;
      letter-spacing: -1px;

      @media screen and (max-width: 1023px) {
        font-size: 32px;
        line-height: 36px;
      }

      @media screen and (max-width: 767px) {
        font-size: 28px;
        line-height: 32px;
      }

      ${({ $index, $bannerCount }) => (($bannerCount === 2 || $bannerCount === 4)
          && css`
            font-size: 32px;
            line-height: 36px;
          `)
        || (($bannerCount === 3 && ($index === 1 || $index === 2))
          && css`
            font-size: 32px;
            line-height: 36px;
          `)}
    }

    &.title-subheading {
      width: 100%;
      font-size: 24px;
      line-height: 24px;
      font-weight: 500;

      @media screen and (max-width: 1023px) {
        font-size: 18px;
        line-height: 20px;
      }

      ${({ $index, $bannerCount }) => (($bannerCount === 2 || $bannerCount === 4)
          && css`
            font-size: 18px;
            line-height: 20px;
          `)
        || (($bannerCount === 3 && ($index === 1 || $index === 2))
          && css`
            font-size: 18px;
            line-height: 20px;
          `)}
    }

    &.title-points {
      width: 100%;
      font-size: 12px;
      line-height: 12px;
      font-weight: normal;
      margin-bottom: 0;
    }

    &.points {
      position: relative;
      color: ${variables.colorRed};
      font-size: 80px;
      line-height: 80px;
      font-weight: 600;
      letter-spacing: -1px;
      margin-bottom: 10px;
      width: auto;

      .hero-carousel {
        ${({ $bannerCount }) => $bannerCount === 1 && 'align-self: center;'}
      }

      @media screen and (max-width: 1023px) {
        font-size: 48px;
        line-height: 48px;
      }

      ${({ $index, $bannerCount }) => (($bannerCount === 2 || $bannerCount === 4)
        && css`
          font-size: 48px;
          line-height: 48px;
        `)
      || (($bannerCount === 1)
        && css`
          font-size: 80px;
          line-height: 80px;

          @media screen and (max-width: 1023px) {
            font-size: 48px;
            line-height: 48px;
          }
        `)
      || (($bannerCount === 3 && ($index === 1 || $index === 2))
        && css`
          font-size: 48px;
          line-height: 48px;
        `)}

      &:after {
        position: absolute;
        width: 1.75rem;
        height: 1.75rem;
        top: 10px;
        content: '';
        background-image: url('/pts.svg');
        background-size: 1.75rem;
        background-repeat: no-repeat;

        @media screen and (max-width: 1023px) {
          top: 5px;
          background-size: 1.5rem;
        }

        ${({ $index, $bannerCount }) => (($bannerCount === 2 || $bannerCount === 4)
          && css`
            top: 5px;
            background-size: 1.5rem;
          `)
        || (($bannerCount === 1)
          && css`
            @media screen and (max-width: 1023px) {
              top: 5px;
              background-size: 1.5rem;
            }
          `)
        || (($bannerCount === 3 && ($index === 1 || $index === 2))
          && css`
            top: 5px;
            background-size: 1.5rem;
          `)}
      }
    }

    &.descripton {
      width: 100%;
      font-size: 20px;
      line-height: 24px;
      font-weight: normal;
      padding-bottom: 1rem;
      text-align: start;

      @media screen and (max-width: 1023px) {
        font-size: 18px;
        line-height: 20px;
      }

      ${({ $index, $bannerCount }) => (($bannerCount === 2 || $bannerCount === 4)
          && css`
            font-size: 18px;
            line-height: 20px;
          `)
        || (($bannerCount === 3 && ($index === 1 || $index === 2))
          && css`
            font-size: 18px;
            line-height: 20px;
          `)}
    }
  }
`;

export const ImageWrapper = styled.div<ImageWrapperProps>`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  height: 100%;
  position: relative;
  background-color: ${(props) => props.$bgColor ? variables[props.$bgColor] : variables.colorGreyLight} !important;
  order: 2;
  justify-content: start;
  align-items: start;
  width: 50% !important;
  order: ${(props) => props.$imagePosition === 'align-right' ? 2 : 1};

  @media screen and (max-width: 1023px) {
    justify-content: center;
    align-items: center;
  }

  @media screen and (max-width: 640px) {
    order: 1;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100% !important;
    margin: 0;
  }

  ${({ $index, $bannerCount }) => ($index === 0 && $bannerCount === 3
      && css`
        width: 100% !important;
        order: 2;
        align-items: center;
        justify-content: center;

        @media screen and (max-width: 1023px) {
          margin: 0 auto;
        }

        @media screen and (max-width: 640px) {
          order: 1;
        }
      `) || ($bannerCount === 1
        && css`
          width: 50%;
          height: auto;
        `
  )}

  img {
    height: 100%;
    align-self: stretch;
    width: auto;

    @media screen and (max-width: 640px) {
      height: auto;
    }

    @media screen and (max-width: 375px) {
      height: auto;
      width: auto;
    }
  }
`;

export const DesktopBannerImage = styled(Image)`
  display: block !important;
  grid-area: 1/1;
  width: 100%;
  height: 100%;
  @media screen and (max-width: 1023px) {
    display: none !important;
  }
`;

export const TabletBannerImage = styled(Image)`
  display: none !important;
  grid-area: 1/1;
  width: 100%;
  height: 100%;
  @media screen and (max-width: 1023px) {
    display: block !important;
  }
  @media screen and (max-width: 767px) {
    display: none !important;
  }
`;

export const MobileBannerImage = styled(Image)`
  display: none !important;
  grid-area: 1/1;
  width: 100%;
  height: 100%;
  @media screen and (max-width: 767px) {
    display: block !important;
  }
`;

export const CtaContainer = styled.div<CtaContainerProps>`
  margin: 0 auto;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0 !important;

  &.hero-carousel {
    ${({ $bannerCount, $imagePosition }) => ($bannerCount === 1 && css`
        @media screen and (min-width: 1024px) {
          margin-left: 0px;
          justify-content: ${$imagePosition === 'align-right' ? 'flex-start' : 'center'};
        }
        @media screen and (min-width: 768px) and (max-width: 1023px) {
          margin-left: initial;
          margin-right: initial;
        }
        @media screen and (max-width: 767px) {
          justify-content: flex-start;
        }
    `)}
  }
`;
